import React, { useState } from 'react';
import axiosInstance from '../../../interceptor/axiosInstance';
import Swal from 'sweetalert2';
import { AsyncPaginate } from 'react-select-async-paginate';
import ReactDOM from 'react-dom';
import './AddProgram.css';

const AddProgram = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [totalSemesters, setTotalSemesters] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
  };

  const loadDepartments = async (searchQuery, loadedOptions, { page }) => {
    try {
      const res = await axiosInstance.get(`programs/departments/?search=${searchQuery}&page=${page}`);
      const departments = res.data?.data?.departments || [];

      return {
        options: departments.map((dept) => ({
          value: dept.id,
          label: dept.name,
        })),
        hasMore: !!res.data?.data?.pagination?.next,
        additional: {
          page: page + 1,
        },
      };
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name || !totalSemesters || !selectedDepartment) {
      setError('All fields are required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axiosInstance.post('programs/programs/', {
        name,
        total_semesters: totalSemesters,
        department: selectedDepartment.value,
      });

      Swal.fire({
        title: 'Success!',
        text: 'Program added successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        setName('');
        setTotalSemesters('');
        setSelectedDepartment(null);
        onSuccess();
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Something went wrong.';
      setError(errorMessage);

      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Ok',
      });
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="add-program-modal-overlay" onClick={onClose}>
      <div className="add-program-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-program-modal-header">
          <div>
            <h2 className="add-program-modal-title">Add New Program</h2>
            <p className="add-program-modal-subtitle">Create a new program in the system</p>
          </div>
          <button className="add-program-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="add-program-modal-form">
          {/* Program Name */}
          <div className="add-program-modal-field">
            <label className="add-program-modal-label">
              Program Name <span className="add-program-modal-required">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="add-program-modal-input"
              placeholder="Enter program name..."
              required
            />
          </div>

          {/* Total Semesters */}
          <div className="add-program-modal-field">
            <label className="add-program-modal-label">
              Total Semesters <span className="add-program-modal-required">*</span>
            </label>
            <input
              type="number"
              value={totalSemesters}
              onChange={(e) => setTotalSemesters(e.target.value)}
              className="add-program-modal-input"
              placeholder="Enter number of semesters..."
              min="1"
              required
            />
          </div>

          {/* Department */}
          <div className="add-program-modal-field">
            <label className="add-program-modal-label">
              Department <span className="add-program-modal-required">*</span>
            </label>
            <AsyncPaginate
              value={selectedDepartment}
              loadOptions={loadDepartments}
              onChange={handleDepartmentChange}
              additional={{ page: 1 }}
              isSearchable
              placeholder="Search and select department..."
              className="add-program-modal-select"
              classNamePrefix="add-program-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 100002,
                }),
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="add-program-modal-error">
              <span className="add-program-modal-error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="add-program-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="add-program-modal-btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="add-program-modal-btn-submit"
            >
              {loading ? (
                <>
                  <span className="add-program-modal-spinner"></span>
                  Adding...
                </>
              ) : (
                <>
                  <span>✓</span>
                  Add Program
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AddProgram;
